import { FC, useState, useEffect } from 'react'
import { Button, Form, Modal, Nav, Alert } from 'react-bootstrap'
import ReCAPTCHA from 'react-google-recaptcha'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'

// hooks
import { useApi } from 'landing-page/hooks/useApi'
import { api } from 'landing-page/types/api'

// assets
import lpdpTalenta from 'landing-page/assets/img/lpdp-talenta.png'

interface AuthFormData {
    nama_depan?: string;
    nama_belakang?: string;
    register_umum?: boolean;
    email: string;
    password: string;
    g_recaptcha_response?: string;
}

interface AuthModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
    // onRegisterSuccess: () => void;
}

const AuthModal: FC<AuthModalProps> = ({ 
    show = false, onHide, onSuccess
}) => {
    const [showModal, setShowModal] = useState(show)

    const [activeTab, setActiveTab] = useState('alumni')
    
    const [showPassword, setShowPassword] = useState(false)
    
    const [formData, setFormData] = useState<AuthFormData>({
        email: '',
        password: '',
        g_recaptcha_response: '',
    })

    const [errors, setErrors] = useState<{ 
        recaptcha?: string;
        email?: string;
        password?: string;
        general?: string;
        nama_depan?: string;
        nama_belakang?: string;
    }>({});

    const [isMasyarakatDaftar, setIsMasyarakatDaftar] = useState(false)

    const { data, execute: executeLogin, loading, error, reset: resetLogin } = useApi(api.loginThesis.bind(api));

    const { data: dataAlumni, execute: executeAlumni, loading: loadingAlumni, error: errorAlumni, reset: resetAlumni } = useApi(api.loginAlumni.bind(api));

    const { data: dataRegister, execute: executeRegister, loading: loadingRegister, error: errorRegister, reset: resetRegister } = useApi(api.registerThesis.bind(api));

    const handleClose = () => {
        setShowModal(false);
        setErrors({});
        resetLogin();
        resetAlumni();
        resetRegister();
        onHide();
    }

    const handleChangeCaptcha = (value: string | null) => {
        setErrors(prev => ({ ...prev, recaptcha: undefined }));
        setFormData({ ...formData, ['g_recaptcha_response']: value || '' });
    }

    const handleTabChange = (tab: string, isMasyarakatDaftar: boolean = false) => {
        setActiveTab(tab);
        setErrors({});
        resetLogin();
        resetAlumni();
        resetRegister();
        if (tab === 'masyarakat') {
            setIsMasyarakatDaftar(isMasyarakatDaftar);
            setFormData({
                nama_depan: isMasyarakatDaftar ? '' : undefined,
                nama_belakang: isMasyarakatDaftar ? '' : undefined,
                email: '',
                password: '',
                g_recaptcha_response: '',
            } as AuthFormData);
        } else {
            setFormData({
                email: '',
                password: '',
                g_recaptcha_response: '',
            } as AuthFormData);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear field-specific errors when user starts typing
        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Reset previous errors
        setErrors({});
        resetLogin();
        resetAlumni();
        resetRegister();

        // Validate form and get validation errors
        const newErrors: typeof errors = {};

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = 'Email wajib diisi';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Format email tidak valid';
        }

        // Password validation
        if (!formData.password.trim()) {
            newErrors.password = 'Password wajib diisi';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password minimal 6 karakter';
        }

        // Captcha validation for masyarakat
        if (!formData.g_recaptcha_response) {
            newErrors.recaptcha = 'Captcha wajib diisi';
        }

        // Nama depan validation for masyarakat registration only
        if (activeTab === 'masyarakat' && isMasyarakatDaftar && !formData.nama_depan?.trim()) {
            newErrors.nama_depan = 'Nama depan wajib diisi';
        }

        // Nama belakang validation for masyarakat registration only
        if (activeTab === 'masyarakat' && isMasyarakatDaftar && !formData.nama_belakang?.trim()) {
            newErrors.nama_belakang = 'Nama belakang wajib diisi';
        }

        // If there are validation errors, set them and return early
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            const errorMessage = newErrors.email || newErrors.password || newErrors.recaptcha || newErrors.general;
            toast.error(errorMessage);
            return;
        }

        try {
            // Prepare login data
            const loginData = {
                nama_depan: activeTab === 'masyarakat' && isMasyarakatDaftar ? formData.nama_depan : undefined,
                nama_belakang: activeTab === 'masyarakat' && isMasyarakatDaftar ? formData.nama_belakang : undefined,
                register_umum: activeTab === 'masyarakat' && isMasyarakatDaftar ? true : false,
                email: formData.email.trim(),
                password: formData.password,
                g_recaptcha_response: formData.g_recaptcha_response,
            };

            // Execute login
            if (activeTab === 'alumni') {
                await executeAlumni(loginData)
            } else if (activeTab === 'masyarakat') {
                await executeLogin(loginData)

                if (isMasyarakatDaftar) {
                    await executeRegister(loginData)
                }
            }
        } catch (error: any) {
            // Handle specific error cases
            if (error?.response?.data?.message) {
                setErrors({ general: error.response.data.message });
            } else if (error?.message) {
                setErrors({ general: error.message });
            } else {
                setErrors({ general: 'Terjadi kesalahan saat login. Silakan coba lagi.' });
            }

            toast.error(errors?.general || 'Terjadi kesalahan saat login. Silakan coba lagi.');
        }
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Sync internal state with prop changes
    useEffect(() => {
        setShowModal(show);
    }, [show]);

    useEffect(() => {
        if (data?.data) {
            // Call onSuccess callback and close modal
            onSuccess();
            handleClose();
        }
    }, [data]);

    useEffect(() => {
        if (dataRegister?.meta) {
            onSuccess();
            // handleClose();

            // reset register relate state and back to login tab and the state
            resetRegister();
            handleTabChange('masyarakat', false);

            toast.success(dataRegister.meta.message || 'Registrasi berhasil');
        }
    }, [dataRegister]);

    useEffect(() => {
        if (error?.status === 400) {
            setErrors({ general: error.message });
            toast.error(error.message);
        }
    }, [error]);

    useEffect(() => {
        if (errorAlumni?.status === 400) {
            setErrors({ general: errorAlumni.message });
            toast.error(errorAlumni.message);
        }
    }, [errorAlumni]);

    useEffect(() => {
        if (dataAlumni?.data) {
            onSuccess();
            handleClose();
        }
    }, [dataAlumni]);

    return (
        <Modal
            show={showModal}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header>
                <div className='d-flex align-items-center justify-content-center w-100'>
                    <img width={200} height={35} src={lpdpTalenta} alt="lpdp-talenta" />
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className='mb-5'>
                    <p className='fs-5 text-center m-0'>Pilih peranmu untuk melanjutkan:</p>
                    <p className='fs-5 text-center fw-bold m-0'>Alumni LPDP atau Masyarakat Umum</p>
                </div>

                <Nav justify variant="tabs" className='mb-3'>
                    <Nav.Item>
                        <Nav.Link 
                            href='#' 
                            active={activeTab === 'alumni'}
                            onClick={() => handleTabChange('alumni', false)}
                        >
                            Alumni
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            href='#' 
                            active={activeTab === 'masyarakat'}
                            onClick={() => handleTabChange('masyarakat', false)}
                        >
                            Umum
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                {/* Error Alert */}
                {errors.general && (
                    <Alert variant="danger" className="mb-3">
                        {errors.general}
                    </Alert>
                )}

                {/* Login Form */}
                <Form onSubmit={handleSubmit}>
                    { activeTab === 'masyarakat' && isMasyarakatDaftar && (
                        <>
                            <Form.Group className='mb-3'>
                                <Form.Label>Nama Depan <span className='text-danger'>*</span></Form.Label>
                                <Form.Control 
                                    type='text' 
                                    name='nama_depan' 
                                    value={formData.nama_depan}
                                    onChange={handleInputChange}
                                    placeholder='Masukkan nama depan Anda' 
                                    className={`mb-2 ${errors.nama_depan ? 'is-invalid' : ''}`}
                                    required
                                    disabled={loading || loadingAlumni}
                                />
                                {errors.nama_depan && (
                                    <Form.Text className='text-danger small'>{errors.nama_depan}</Form.Text>
                                )}
                            </Form.Group>
                            <Form.Group className='mb-3'>
                                <Form.Label>Nama Belakang <span className='text-danger'>*</span></Form.Label>
                                <Form.Control 
                                    type='text' 
                                    name='nama_belakang' 
                                    value={formData.nama_belakang}
                                    onChange={handleInputChange}
                                    placeholder='Masukkan nama belakang Anda' 
                                    className={`mb-2 ${errors.nama_belakang ? 'is-invalid' : ''}`}
                                    required
                                    disabled={loading || loadingAlumni}
                                />
                                {errors.nama_belakang && (
                                    <Form.Text className='text-danger small'>{errors.nama_belakang}</Form.Text>
                                )}
                            </Form.Group>
                        </>
                    )}

                    <Form.Group className='mb-3'>
                        <Form.Label>Email <span className='text-danger'>*</span></Form.Label>
                        <Form.Control 
                            type='email' 
                            name='email'
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder='Masukkan email Anda' 
                            className={`mb-2 ${errors.email ? 'is-invalid' : ''}`}
                            required
                            disabled={loading || loadingAlumni}
                        />
                        {errors.email && (
                            <Form.Text className='text-danger small'>{errors.email}</Form.Text>
                        )}
                        {activeTab === 'alumni' && !errors.email && (
                            <Form.Text className='text-muted small'>
                                Gunakan email yang sudah terdaftar di LPDP
                            </Form.Text>
                        )}
                    </Form.Group>

                    <Form.Group className='mb-3'>
                        <Form.Label>Password <span className='text-danger'>*</span></Form.Label>
                        <div className='position-relative'>
                            <Form.Control 
                                type={showPassword ? 'text' : 'password'} 
                                name='password'
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder='Masukkan password Anda' 
                                className={errors.password ? 'is-invalid' : ''}
                                required
                                disabled={loading || loadingAlumni}
                            />
                            <Button
                                variant='link'
                                className='position-absolute end-0 top-0 h-100 border-0'
                                onClick={togglePasswordVisibility}
                                style={{ zIndex: 10 }}
                                disabled={loading || loadingAlumni}
                            >
                                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                            </Button>
                        </div>
                        {errors.password && (
                            <Form.Text className='text-danger small'>{errors.password}</Form.Text>
                        )}
                    </Form.Group>

                    <div className='d-flex justify-content-end align-items-center'>
                        { activeTab === 'alumni' ? (
                            <a
                                href='https://beasiswalpdp.kemenkeu.go.id/index.php/site/resetPassword'
                                className='text-decoration-none text-primary'
                                target='_blank'
                                rel='noreferrer'
                            >
                                Lupa Password?
                            </a>
                        ) : (
                            <a href='#' className='text-decoration-none text-primary'>
                                Lupa Password?
                            </a>
                        )}
                    </div>

                    <Form.Group className='mt-3'>
                        <ReCAPTCHA
                            className='g-recaptcha mb-2'
                            sitekey="6Lf3qh8qAAAAAAmCYQOBXog8koATjbM4w7HZBGo2"
                            onChange={handleChangeCaptcha}
                        />
                        {errors?.recaptcha && (
                            <Form.Text className='text-danger small'>{errors?.recaptcha}</Form.Text>
                        )}
                    </Form.Group>
                        
                    <Button 
                        variant='primary' 
                        type='submit' 
                        className='w-100 rounded-pill mt-5'
                        disabled={loading || loadingAlumni}
                    >
                        {loading || loadingAlumni ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Memproses...
                            </>
                        ) : (
                            activeTab === 'masyarakat' && isMasyarakatDaftar ? 'Daftar' : 'Masuk'
                        )}
                    </Button>

                    <div className='d-flex justify-content-center mt-5' style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <p className='m-0'>
                            { activeTab === 'masyarakat' ? (
                                isMasyarakatDaftar ? 'Sudah Punya Akun?' : 'Belum Punya Akun?'
                            ) : (
                                'Belum Punya Aktivasi?'
                            )}
                        </p>
                        {activeTab === 'alumni' && (
                            <Link to={'/activation'} className='text-primary mx-3'>Ajukan permintaan aktivasi</Link>
                        )}
                        {activeTab === 'masyarakat' && !isMasyarakatDaftar && (
                            // <Link to={'/thesis/register'} className='text-primary mx-3'>Daftar</Link>
                            <a href='#' onClick={() => handleTabChange('masyarakat', true)} className='text-primary mx-3'>Daftar</a>
                        )}
                        {activeTab === 'masyarakat' && isMasyarakatDaftar && (
                            <a href='#' onClick={() => handleTabChange('masyarakat', false)} className='text-primary mx-3'>Masuk</a>
                        )}
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer className='align-items-center justify-content-center'>
                <p className='text-center text-muted small'>
                    Kami melayani dengan integritas, menjaga amanah bangsa tanpa kompromi.
                </p>
            </Modal.Footer>
        </Modal>
    )
}

export default AuthModal